import * as Sentry from '@sentry/nextjs';
import { observer } from 'mobx-react-lite';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { IFooter } from '@features/footer/typescript';
import { ENavTransparency, INavigation, INavParams } from '@features/navigation/typescript';
import { TCategoriesTree } from '@features/categories/typescript';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import { useCartStore } from '@features/cart/mobx/context/CartStoreProvider';
import { useCustomerDataStore } from '@features/customerData/mobx/context/CustomerDataStoreProvider';
import styles from '@features/layout/components/Layout/Layout.styles';
import { getRegion, removeConfiguratorScripts } from '@utils/misc';
import getPublicEnv from '@utils/getPublicEnv';
import { logScrollTrigger } from '@utils/scrollDebug';
import useAuth from '@hooks/useAuth';
import useHashScrollAfterRoute from '@hooks/useHashScrollAfterRoute';
import alertsStore from '@context/global/mobx/alertStore';
import Navigation from '@features/navigation/components/Navigation/Navigation';
import ReaffiliatesNewSignIn from '@reaffiliates/ReaffiliatesNewSignIn';
import SimpleLayout from '@features/layout/components/Layout/components/SimpleLayout/SimpleLayout';
import NavigationContainer from '@features/layout/components/Layout/components/NavigationContainer/NavigationContainer';
import Footer from '@features/footer/Footer';
import useBodyOverflow from '@hooks/useBodyOverflow';
import useServiceMap from '@hooks/useServiceMap';
import LangPopup from '@components/LangPopup/LangPopup';
import PromotionalMessages from '@components/PromotionalMessages/PromotionalMessages';
import { IAddressCollection } from '@features/checkout/typescript';
import { IAdditionalArgument } from '@features/cart/typescript';
import { useCartSync } from '@hooks/useCartSync';
import CmsPromotionalMessage from '@features/cms/objects/CmsPromotionalMessage';
import { ICmsPromotionalMessage } from '@features/cms/typescript';
import theme from '@styles/theme';
import { ROLES } from '@config/index';
import SentryToolbar from '@components/SentryToolbar/SentryToolbar';
import { getSentryToolbarConfig } from '@config/sentry-toolbar';
import GlobalTooltipProvider from '@components/GlobalTooltipProvider/GlobalTooltipProvider';
import DesignStorModal from '@components/DesignStorModal/DesignStorModal';
import InteriorDesingModal from '@components/InteriorDesingModal/InteriorDesingModal';
const DynamicCartMiniSideBar = dynamic(() => import('@features/cart/components/CartSideBar/CartMiniSideBar'), {
  ssr: false
});
const DynamicAlerts = dynamic(() => import('@components/Alerts/Alerts'), {
  ssr: false
});
const DynamicPopups = dynamic(() => import('@components/Popups/Popups'), {
  ssr: false
});
type PropsType = {
  children: React.ReactNode;
  footer?: IFooter;
  navigationBar?: INavigation;
  simpleLayout?: boolean;
  attachFirstChildToNavigation?: boolean;
  navParams?: INavParams;
  categoriesTree?: TCategoriesTree;
  promotionalMessages?: ICmsPromotionalMessage[];
  addresses?: IAddressCollection;
  overflowY?: string;
  overflowX?: string;
  slugToPageScrollY?: string;
};
const Layout = ({
  children,
  footer = {} as IFooter,
  navigationBar = {} as INavigation,
  simpleLayout = false,
  attachFirstChildToNavigation = false,
  navParams = {
    navTransparency: ENavTransparency.BOTH_NOT_TRANSPARENT
  } as INavParams,
  categoriesTree = {} as TCategoriesTree,
  promotionalMessages = [],
  addresses = undefined as IAddressCollection | undefined,
  overflowY = undefined,
  overflowX = undefined,
  slugToPageScrollY = undefined as string | undefined
}: PropsType) => {
  useCartSync();
  const {
    data: session
  } = useSession();

  // Sprawdź czy użytkownik ma rolę ECOMMERCE_DEVELOPER
  const isEcommerceDeveloper = session?.user?.roles?.includes(ROLES.ECOMMERCE_DEVELOPER);
  const {
    navigationProductItemSelected,
    navigationAndFooterVisible,
    isCartCreated,
    setCartCreated,
    isMenuMobileOpened,
    isVideoModalOpened,
    isSearchOpened,
    userData,
    langPopupHeight,
    isPromotionalMessagesOpened,
    barPositionStatic,
    isMenuPined,
    isLoginGeneralOpened,
    userPanelOpened,
    setIsPromotionalMessagesOpened,
    setBarsPositionStatic,
    isProductCardOpened,
    lastVisitedProductId,
    navigationContainerIsStatic,
    setNavigationContainerIsStatic,
    navigationContainerIsVisible,
    setNavigationContainerIsVisible,
    setNavigationContainerHeight
  } = layoutUiState;
  const region = getRegion();
  const [langPopupIsActive, setLangPopupIsActive] = useState(false);
  const [isDesignStorModalOpen, setIsDesignStorModalOpen] = useState<boolean>(false);
  const [isInteriorDesingModalOpen, setIsInteriorDesingModalOpen] = useState<boolean>(false);
  const {
    cartId,
    fetchAndPopulateCartStore,
    fetchAvailableDeliveries,
    getCartFromLocalStorage
  } = useCartStore();
  const router = useRouter();
  const {
    pathname
  } = router;
  useHashScrollAfterRoute(router);
  const {
    id: customerID
  } = useCustomerDataStore();
  const {
    createSession
  } = useAuth();
  const serviceMap = useServiceMap(categoriesTree);
  const getPromotionalMessages = (): CmsPromotionalMessage[] => {
    if (promotionalMessages.length) {
      return promotionalMessages.map(promotionalMessage => new CmsPromotionalMessage(promotionalMessage));
    }
    return [];
  };
  const handleNewSignInGTM = () => {
    const delayMaxTime = 1000 * 10;
    const isWithinDelay = session?.token?.newSignIn && typeof session.token.newSignIn === 'number' && Date.now() - session.token.newSignIn < delayMaxTime;
    if (isWithinDelay) ReaffiliatesNewSignIn(customerID);
  };

  // scroll to the saved position on the page
  const handleClick = (event: MouseEvent) => {
    const anchor = (event.target as HTMLElement | null)?.closest('a');
    if (anchor?.href?.includes('/product/')) {
      logScrollTrigger('Layout:documentClick — skip save (product link)', {
        slugToPageScrollY,
        href: anchor.href
      });
      return;
    }
    if (slugToPageScrollY && typeof window !== 'undefined' && document.body.offsetHeight && window.scrollY) {
      logScrollTrigger('Layout:documentClick — save pagesScrollY', {
        slugToPageScrollY,
        scrollY: window.scrollY,
        documentHeight: document.body.offsetHeight,
        lastVisitedProductId
      });
      layoutUiState.setScrollYOnPage(slugToPageScrollY, document.body.offsetHeight, window.scrollY);
    }
  };
  useEffect(() => {
    if (slugToPageScrollY && typeof window !== 'undefined') {
      const hasUrlFragment = window.location.hash.length > 1;
      const scrollYData = layoutUiState.getScrollYOnPage(slugToPageScrollY);
      let timeoutId: number | undefined;

      // When returning from a product card, ProductList scrolls to lastVisitedProductId —
      // skip pagesScrollY restore to avoid fighting that scroll.
      // Do not depend on lastVisitedProductId — clearing it after forward click re-triggered restore on KP.
      if (!hasUrlFragment && !layoutUiState.lastVisitedProductId && scrollYData && document.body.offsetHeight) {
        timeoutId = window.setTimeout(() => {
          if (layoutUiState.lastVisitedProductId || layoutUiState.listingScrollRestorePending) return;
          if (window.location.pathname.includes('/product/')) {
            logScrollTrigger('Layout:pagesScrollYRestore — aborted (on product page)', {
              slugToPageScrollY,
              pathname: window.location.pathname
            });
            return;
          }
          const freshScrollYData = layoutUiState.getScrollYOnPage(slugToPageScrollY);
          if (!freshScrollYData || !document.body.offsetHeight) return;
          const difference = freshScrollYData.documentHeight - document.body.offsetHeight;
          const scrollYTo = freshScrollYData.scrollY - difference;
          logScrollTrigger('Layout:pagesScrollYRestore — window.scrollTo', {
            slugToPageScrollY,
            scrollYData: freshScrollYData,
            difference,
            scrollYTo
          });
          window.scrollTo({
            top: scrollYTo,
            behavior: 'instant'
          });
        }, 400);
      } else {
        logScrollTrigger('Layout:pagesScrollYRestore — skipped', {
          slugToPageScrollY,
          hasUrlFragment,
          lastVisitedProductId: layoutUiState.lastVisitedProductId,
          listingScrollRestorePending: layoutUiState.listingScrollRestorePending,
          hasScrollYData: Boolean(scrollYData),
          bodyOffsetHeight: document.body.offsetHeight
        });
      }
      window.document.onclick = handleClick;
      return () => {
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        window.document.onclick = null;
      };
    }
    return () => {
      window.document.onclick = null;
    };
  }, [slugToPageScrollY]);
  useEffect(() => {
    if (!pathname.includes('product')) {
      removeConfiguratorScripts();
    }
  }, [pathname]);
  const isDesignStorEnabled = getPublicEnv('NEXT_PUBLIC_DISPLAY_DESIGN_STOR') === 'true';
  const handleHashChange = () => {
    const hash = window.location.hash;
    if (hash.includes('designStor') && getPublicEnv('NEXT_PUBLIC_DISPLAY_DESIGN_STOR') === 'true') {
      setIsDesignStorModalOpen(true);
    } else if (hash.includes('interiorDesing')) {
      setIsInteriorDesingModalOpen(true);
    } else {
      setIsDesignStorModalOpen(false);
      setIsInteriorDesingModalOpen(false);
    }
  };
  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange, false);
    window.addEventListener('popstate', handleHashChange, false);
    window.addEventListener('click', handleHashChange, false);
    handleHashChange();
    return () => {
      window.removeEventListener('hashchange', handleHashChange, false);
      window.removeEventListener('click', handleHashChange, false);
      window.removeEventListener('popstate', handleHashChange, false);
    };
  }, []);
  const removeHash = () => {
    window.history.replaceState({}, '', window.location.pathname);
  };
  useEffect(() => {
    if (session) {
      if (typeof customerID === 'string') handleNewSignInGTM();
      Sentry.getCurrentScope().setTag('customerId', customerID);
    }
  }, [customerID]);
  useEffect(() => {
    const refetchAvailableDeliveries = async () => {
      if (isCartCreated && addresses) {
        let additionalArgument: undefined | IAdditionalArgument;
        if (region === 'MX' && addresses?.basicAddress?.postalCode) {
          additionalArgument = {
            key: 'postalCode',
            value: addresses?.basicAddress.postalCode
          };
        } else if (region === 'EUR' && addresses?.basicAddress?.country) {
          additionalArgument = {
            key: 'countryCode',
            value: addresses?.basicAddress.country
          };
        }
        await fetchAvailableDeliveries(additionalArgument);
      }
    };
    refetchAvailableDeliveries();
  }, [addresses]);
  useEffect(() => {
    const getCart = async () => {
      if (!getCartFromLocalStorage()) return;
      const {
        cartId: fetchedCartId,
        products
      } = await fetchAndPopulateCartStore();
      if (fetchedCartId && products.size > 0) {
        let additionalArgument: undefined | IAdditionalArgument;
        if (region === 'MX' && addresses?.basicAddress?.postalCode) {
          additionalArgument = {
            key: 'postalCode',
            value: addresses?.basicAddress.postalCode
          };
        } else if (region === 'EUR' && addresses?.basicAddress?.country) {
          additionalArgument = {
            key: 'countryCode',
            value: addresses?.basicAddress.country
          };
        }
        await fetchAvailableDeliveries(additionalArgument);
      }
      setCartCreated(true);
    };
    getCart();
  }, []);
  useEffect(() => {
    createSession();
  }, [session]);
  useEffect(() => {
    Sentry.getCurrentScope().setTag('cartId', cartId);
  }, [cartId, langPopupHeight, langPopupIsActive]);
  useEffect(() => {
    setIsPromotionalMessagesOpened(getPromotionalMessages().length > 0 && !isMenuMobileOpened && !isMenuPined && !isLoginGeneralOpened && !userPanelOpened && !isLoginGeneralOpened && !isProductCardOpened);
    setBarsPositionStatic(getPromotionalMessages().length > 0 && !isMenuMobileOpened && !isMenuPined && isProductCardOpened && !isLoginGeneralOpened);
  }, [getPromotionalMessages().length, isMenuMobileOpened, isLoginGeneralOpened, isMenuPined, isLoginGeneralOpened, userPanelOpened, isProductCardOpened]);
  const isAlertVisible = useMemo(() => alertsStore.alerts.length > 0 && alertsStore.currentAlert, [alertsStore.alerts.length]);
  const fixedHeight = useMemo(() => !!(navigationProductItemSelected || isMenuMobileOpened || isLoginGeneralOpened || isSearchOpened || userData || isAlertVisible || isVideoModalOpened), [navigationProductItemSelected, isMenuMobileOpened, isSearchOpened, userData, isAlertVisible, isVideoModalOpened, isLoginGeneralOpened]);
  useBodyOverflow(fixedHeight, 'layout');
  const getPromotionalMessagesHeight = (): number => isPromotionalMessagesOpened ? 33 : 0;
  const getMarginTop = useMemo(() => {
    let marginTop = 0;
    if (isSearchOpened) return marginTop;
    if (langPopupIsActive && !isMenuMobileOpened && !isMenuPined && !userPanelOpened && !isLoginGeneralOpened && langPopupHeight && !barPositionStatic) marginTop += langPopupHeight;
    if (isPromotionalMessagesOpened && !barPositionStatic) marginTop += 33;
    return marginTop;
  }, [langPopupIsActive, langPopupHeight, barPositionStatic, isPromotionalMessagesOpened, isMenuMobileOpened, isMenuPined, isSearchOpened, isLoginGeneralOpened, userPanelOpened]);
  const footerHasKeys = useMemo(() => !!Object.keys(footer).length, [footer]);
  if (simpleLayout) {
    return <SimpleLayout attachFirstChildToNavigation={attachFirstChildToNavigation} overflowY={overflowY} overflowX={overflowX}>
        {children}
      </SimpleLayout>;
  }
  return <div sx={styles.layout(getMarginTop, overflowY || null, overflowX || null)}>
      {isCartCreated && <DynamicCartMiniSideBar isCartCreated={isCartCreated} />}
      <PromotionalMessages isActive={(!!isPromotionalMessagesOpened || barPositionStatic) && !!navigationBar} messages={getPromotionalMessages()} />
      {navigationAndFooterVisible && navigationBar && <>
          <LangPopup active={langPopupIsActive && !isMenuMobileOpened && !isMenuPined && !isLoginGeneralOpened} setActive={setLangPopupIsActive} promotionalMessagesHeight={getPromotionalMessagesHeight()} />
        </>}
      <NavigationContainer positionTop={getMarginTop} isProductCardOpened={isProductCardOpened} navigationContainerIsStatic={navigationContainerIsStatic} setNavigationContainerIsStatic={setNavigationContainerIsStatic} navigationContainerIsVisible={navigationContainerIsVisible} setNavigationContainerIsVisible={setNavigationContainerIsVisible} setNavigationContainerHeight={setNavigationContainerHeight} isMenuMobileOpened={isMenuMobileOpened} isMenuPined={isMenuPined} isSearchOpened={isSearchOpened} isLoginGeneralOpened={isLoginGeneralOpened} userPanelOpened={userPanelOpened}>
        <Navigation navElements={navigationBar} categoriesTree={categoriesTree} serviceMap={serviceMap} navParams={!navigationContainerIsStatic ? {
        navTransparency: ENavTransparency.BOTH_NOT_TRANSPARENT,
        backgroundColor: theme.rawColors.neutralWhite
      } as INavParams : navParams} />
      </NavigationContainer>
      <DynamicAlerts />
      <DynamicPopups />
      {children}
      {navigationAndFooterVisible && footerHasKeys && <Footer footer={footer} />}

      {/* Sentry Toolbar dla developerów */}
      {isEcommerceDeveloper && <SentryToolbar enabled={getSentryToolbarConfig().enabled} organizationSlug={getSentryToolbarConfig().organizationSlug} projectIdOrSlug={getSentryToolbarConfig().projectIdOrSlug} environment={getSentryToolbarConfig().environment} placement={getSentryToolbarConfig().placement} theme={getSentryToolbarConfig().theme} sentryOrigin={getSentryToolbarConfig().sentryOrigin} domId={getSentryToolbarConfig().domId} debug={getSentryToolbarConfig().debug} />}

      {/* Globalny Tooltip Provider */}
      <GlobalTooltipProvider />
      {isDesignStorEnabled && <DesignStorModal isOpen={isDesignStorModalOpen} onClose={() => {
      setIsDesignStorModalOpen(false);
      removeHash();
    }} />}
      <InteriorDesingModal isOpen={isInteriorDesingModalOpen} onClose={() => {
      setIsInteriorDesingModalOpen(false);
      removeHash();
    }} />
    </div>;
};
export default observer(Layout);